import {
  mapActions, mapMutations, mapGetters, mapState,
} from 'vuex';
import {
  TAX_WARRANTS_ACTIONS,
  TAX_WARRANTS_MUTATIONS,
  BASE_CRUD_MUTATIONS,
  SYSTEM_CONFIG_ACTIONS,
  GLOBAL_ACTIONS,
  createDefaultComponentData,
  createDefaultComputedProperties,
  formatWarrantNumber,
  processWarrantItemForUpsert,
  validateWarrantItem,
  postProcessWarrantData,
  urlEncodeObject,
  getOwnerName,
  createDefaultPenaltyDate,
  warrantAmountUtils,
  createTaxInquiryGQLFields,
  getTaxInquiryKeys,
  getCurrentWarrantTaxYear,
} from './taxWarrants.util';
import { makeCriteriaStateNullIfEmptyString } from '../../common-utils/CommonTaxFunctionalities';
import { PROTEST_TAX_TYPES } from '../../common-utils/constants';
import { defaultGridLoading, manageGridLoading } from '../../../../util/shared/vue-global';

// The YY prefix of a new warrant number. Shared by every screen that allocates one so the
// three entry points (PTW list, PTW criteria, TR Work File batch) cannot drift apart.
// Requires `taxSettingItem` and `latestTaxYear` on the component.
const currentWarrantTaxYearComputed = {
  currentWarrantTaxYear() {
    return getCurrentWarrantTaxYear({
      taxSettingItem: this.taxSettingItem,
      latestTaxYear: this.latestTaxYear,
    });
  },
};

export const TaxWarrantsMixin = {
  data() {
    return {
      ...createDefaultComponentData(),
      gridLoading: defaultGridLoading(),
      penaltyDate: createDefaultPenaltyDate(),
    };
  },

  computed: {
    ...mapGetters('base/crud', ['item', 'index', 'criteria', 'isVisible', 'items']),
    ...mapState({
      latestTaxYear: (state) => state.tax.taxCommon.latestTaxYear,
      StoreSelItems: (state) => state.tax.taxWarrants.selectedItems,
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem || {},
      nextWarrantNumber: (state) => state.tax.taxWarrants.nextWarrantNumber,
      nextWarrantSystemID: (state) => state.tax.taxWarrants.nextWarrantSystemID,
    }),

    ...createDefaultComputedProperties(),
    ...currentWarrantTaxYearComputed,

    selectedItems: {
      get() {
        return this.StoreSelItems;
      },
      set(value) {
        this.setSelectedItems(value);
      },
    },

    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },

    editedItem: {
      get() {
        return this.item(this.baseCrudKey) || {};
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
  },

  methods: {
    ...mapMutations('tax/taxWarrants', TAX_WARRANTS_MUTATIONS),
    ...mapMutations('base/crud', BASE_CRUD_MUTATIONS),
    ...mapActions('tax/taxWarrants', TAX_WARRANTS_ACTIONS),
    ...mapActions('tax/taxInquires', ['loadTaxInquires']),
    ...mapActions('SystemConfig', SYSTEM_CONFIG_ACTIONS),
    ...mapActions(GLOBAL_ACTIONS),
    ...mapActions('tax/taxCommon', ['loadTaxYearList']),

    // Utility methods
    formatWarrantNumber,
    processWarrantItemForUpsert,
    validateWarrantItem,
    postProcessWarrantData,
    urlEncodeObject,
    getOwnerName,
    createDefaultPenaltyDate,
    manageGridLoading,

    // Amount utilities
    roundAmount(field) {
      if (!this.editedItem) return;
      const value = warrantAmountUtils.roundAmount(this.editedItem, field);
      this.editedItem[field] = value;
    },

    amountToNum(field) {
      if (!this.editedItem) return;
      const value = warrantAmountUtils.amountToNum(this.editedItem, field);
      this.editedItem[field] = value;
    },

    initializeAmountFormat() {
      if (!this.editedItem) return;
      // Format amount fields if they exist
      const formatFields = ['sherifFeeAmount', 'treasureFeeAmount', 'issueAmount', 'amountCollected'];
      formatFields.forEach((field) => {
        const value = this.editedItem[field];
        // Skip individual field if it's 0, undefined, or null (each checked independently)
        if (value === undefined || value === null || value === 0 || value === '') {
          return;
        }
        this.editedItem[field] = warrantAmountUtils.formatAmount(value);
      });
    },

    setDefaultSelectedItems() {
      this.$nextTick(() => {
        this.selectedItems = [];
      });
    },
    taxWarrantSelectedItem(id) {
      this.selectedItems = id;
    },

    activateTransaction(val) {
      this.activeTransItem = val;
    },
    initCriteria() {
      this.loadConfig({ requiredScope: 'taxConfig' });
      const initialCriteria = this.criteriaState;
      this.criteriaState = {};
      this.criteriaState.taxYear = initialCriteria.taxYear || this.latestTaxYear;
    },
    async handleLoadWarrantList() {
      manageGridLoading(this, true, 'Searching...');
      const { baseCrudKey } = this;

      const returnCriteria = makeCriteriaStateNullIfEmptyString(this.criteriaState);
      this.criteriaState = { ...returnCriteria };
      const criteria = { ...this.criteriaState };

      try {
        await this.loadTaxWarrants({
          criteria,
          baseCrudKey,
          postProcessTrans: this.postProcessData || this.postProcessWarrantData,
        });
        this.setDefaultSelectedItems();
      } finally {
        manageGridLoading(this, false);
      }
    },

    handleSingleOrArrowClick(fromCrud) {
      // Default implementation - can be overridden in components
      this.selectedRowData = { ...fromCrud };
    },
    async imagesLeftClick(leftClick) {
      if (this.isNewItem) {
        this.uploadFileClickParams.leftClickFlag = true;
        this.uploadFileClickParams.leftClickDef = leftClick;
      } else {
        leftClick();
      }
    },

    async imagesRightClick(rightClick) {
      if (this.isNewItem) {
        this.uploadFileClickParams.rightClickFlag = true;
        this.uploadFileClickParams.rightClickDef = rightClick;
      } else {
        rightClick();
      }
    },

    emitSelectedItem(id) {
      this.selectedItems = id;
    },
  },

  watch: {
    latestTaxYear(newVal, oldVal) {
      if (newVal && !oldVal) this.criteriaState.taxYear = this.latestTaxYear;
      if (newVal !== 0) {
        this.isItFirstCallOrNot = true;
        if (this.handleLoadWarrantList) {
          this.handleLoadWarrantList();
        }
      }
    },
  },

  created() {
    // Load initial data
    this.loadLastWarrantNo();
    this.loadLastSystemID();
    this.initCriteria();
  },
};

export const TaxWarrantsDialogMixin = {
  computed: {
    ...mapGetters('base/crud', ['item', 'isVisible']),
    ...mapState({
      latestTaxYear: (state) => state.tax.taxCommon.latestTaxYear,
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem || {},
      nextWarrantNumber: (state) => state.tax.taxWarrants.nextWarrantNumber,
      nextWarrantSystemID: (state) => state.tax.taxWarrants.nextWarrantSystemID,
    }),
    ...currentWarrantTaxYearComputed,

    // This will be overridden by components if they need different CRUD keys
    editedItem: {
      get() {
        return this.item(this.getEditedItemCrudKey()) || {};
      },
      set(value) {
        this.setItem([this.getEditedItemCrudKey(), value]);
      },
    },

    visibility() {
      return this.isVisible(this.getEditedItemCrudKey());
    },

    isVisibleProp() {
      const visibleFlag = this.isVisible(this.getEditedItemCrudKey());
      return visibleFlag !== undefined ? visibleFlag : false;
    },
  },

  methods: {
    ...mapMutations('base/crud', ['setItem']),
    ...mapActions('tax/taxWarrants', TAX_WARRANTS_ACTIONS),
    ...mapActions('tax/taxInquires', ['loadTaxInquires']),
    ...mapActions('SystemConfig', SYSTEM_CONFIG_ACTIONS),
    ...mapActions(GLOBAL_ACTIONS),
    ...mapActions(['flashWarn', 'flashSuccess']),
    ...mapActions('files', ['loadAttachedFiles']),

    formatWarrantNumber,

    // Method to determine which CRUD key to use for editedItem
    // Components can override this method if they use different keys
    getEditedItemCrudKey() {
      return this.dialogCrudKey || this.baseCrudKey;
    },

    // Method to determine which CRUD key to use for upsert operations
    // Components can override this method if they use different keys for upserts
    getUpsertCrudKey() {
      return this.baseCrudKey;
    },

    // Common tax inquiry implementation
    async handleTaxInquiry() {
      const currentEditedItem = this.editedItem;
      if (!currentEditedItem) return;

      this.errorMessage = null;
      const gqlField = createTaxInquiryGQLFields();
      const keys = getTaxInquiryKeys();

      if (!currentEditedItem.taxYear || !currentEditedItem.taxId) return;

      const criteria = { taxYear: currentEditedItem.taxYear, taxId: currentEditedItem.taxId };

      try {
        const data = await this.loadTaxInquires({ criteria, baseCrudKey: 'taxInquiryData', fields: gqlField });
        if (data.length > 0) {
          const [item] = (data || []);
          const typeIsTaxWarrant = PROTEST_TAX_TYPES.includes(item.taxType);

          // Set warning message but don't block the operation
          this.errorMessage = !typeIsTaxWarrant ? '*This record is not related to Tax Warrant' : null;

          // Update the editedItem with tax inquiry data
          const updatedItem = { ...currentEditedItem };
          keys.forEach((key) => {
            if (item[key] !== undefined && item[key] !== null) {
              updatedItem[key] = item[key];
            }
          });
          updatedItem.taxInquiryTransaction = item._id;

          // Set the updated item back to the store
          this.setItem([this.getEditedItemCrudKey(), updatedItem]);
        } else {
          // Clear tax inquiry data
          const updatedItem = { ...currentEditedItem };
          keys.forEach((key) => {
            updatedItem[key] = null;
          });
          this.setItem([this.getEditedItemCrudKey(), updatedItem]);
          this.flashWarn('No tax record found!');
        }
      } catch (e) {
        // Clear tax inquiry data on error
        const updatedItem = { ...currentEditedItem };
        keys.forEach((key) => {
          updatedItem[key] = null;
        });
        this.setItem([this.getEditedItemCrudKey(), updatedItem]);
      }
    },

    // Common upsert method with customizable hooks
    async upsertTransaction(item) {
      // Clear any existing error messages at the start
      this.errorMessage = null;

      const currentEditedItem = this.editedItem;
      const targetItem = item || currentEditedItem;

      if (!targetItem) {
        this.flashWarn('No item data available for processing');
        return;
      }

      // Use utility function for validation
      const validation = validateWarrantItem(targetItem);
      if (!validation.isValid) {
        this.flashWarn(validation.message);
        return;
      }

      // Ensure taxInquiryTransaction is available
      if (!targetItem.taxInquiryTransaction) {
        await this.handleTaxInquiry();
        // Get the updated editedItem after handleTaxInquiry
        const updatedEditedItem = this.editedItem;
        if (!updatedEditedItem.taxInquiryTransaction) {
          this.flashWarn('Please ensure valid Tax ID and Tax Year are entered.');
          return;
        }
        // Use the updated editedItem as targetItem since handleTaxInquiry already updated it
        Object.assign(targetItem, updatedEditedItem);
      }

      try {
        // Use utility function for processing
        const updatedItem = processWarrantItemForUpsert({ ...targetItem });

        // Set taxInquiryTransaction from current editedItem if needed
        const currentEditedItemAfterInquiry = this.editedItem;
        if (currentEditedItemAfterInquiry.taxInquiryTransaction) {
          if (currentEditedItemAfterInquiry.taxInquiryTransaction._id) {
            // eslint-disable-next-line max-len
            updatedItem.taxInquiryTransaction = currentEditedItemAfterInquiry.taxInquiryTransaction._id;
          } else {
            updatedItem.taxInquiryTransaction = currentEditedItemAfterInquiry.taxInquiryTransaction;
          }
        }

        // Ensure baseId is set for GraphQL validation
        if (!updatedItem.baseId && currentEditedItemAfterInquiry.baseId) {
          updatedItem.baseId = currentEditedItemAfterInquiry.baseId;
        }

        // Call pre-upsert hook if available
        if (this.beforeUpsert) {
          await this.beforeUpsert(updatedItem);
        }

        await this.upsertWarrants({ item: updatedItem, baseCrudKey: this.getUpsertCrudKey() });

        // Clear any error messages on successful upsert
        this.errorMessage = null;

        // Call post-upsert hook if available
        if (this.afterUpsert) {
          await this.afterUpsert(updatedItem);
        }

        // Default post-upsert actions
        this.loadLastWarrantNo();
        this.loadLastSystemID();

        if (this.handleLoadWarrantList) {
          this.handleLoadWarrantList();
        }

        if (this.setDefaultSelectedItems) {
          this.setDefaultSelectedItems();
        }
      } catch (error) {
        this.flashWarn('An error occurred while saving the warrant. Please try again.');
      }
    },

    // Common amount formatting methods
    roundAmount(field) {
      if (!this.editedItem) return;
      const value = warrantAmountUtils.roundAmount(this.editedItem, field);
      this.editedItem[field] = value;
    },

    amountToNum(field) {
      if (!this.editedItem) return;
      const value = warrantAmountUtils.amountToNum(this.editedItem, field);
      this.editedItem[field] = value;
    },

    initializeAmountFormat() {
      if (!this.editedItem) return;
      // Format amount fields if they exist
      const formatFields = ['sherifFeeAmount', 'treasureFeeAmount', 'issueAmount', 'amountCollected'];
      formatFields.forEach((field) => {
        const value = this.editedItem[field];
        // Skip individual field if it's 0, undefined, or null (each checked independently)
        if (value === undefined || value === null || value === 0 || value === '') {
          return;
        }
        this.editedItem[field] = warrantAmountUtils.formatAmount(value);
      });
    },

    // Common file loading method
    loadFiles() {
      if (!this.attachmentConstants || !this.editedItem._id) {
        return;
      }
      this.loadAttachedFiles({
        bucketName: this.attachmentConstants.bucketName,
        program: this.attachmentConstants.program,
        parentObjectType: this.attachmentConstants.parentObjectType,
        parentObjectId: this.editedItem._id,
      });
    },

    // Method to clear error messages
    clearErrorMessage() {
      this.errorMessage = null;
    },

    dataFromCommonDropDowns(val, whichField) {
      if (!this.editedItem) return;
      this.editedItem[whichField] = val;
      if (this.handleTaxInquiry) {
        this.handleTaxInquiry();
      }
    },
  },

  watch: {
    isVisibleProp(newVal) {
      if (newVal) {
        if (this.editedItem && this.editedItem._id && this.loadFiles) {
          this.loadFiles();
        }
        if (this.initializeAmountFormat) {
          this.initializeAmountFormat();
        }
      }
    },
  },
};

export const TaxWarrantsCriteriaMixin = {
  computed: {
    ...mapState({
      latestTaxYear: (state) => state.tax.taxCommon.latestTaxYear,
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem || {},
    }),
    ...currentWarrantTaxYearComputed,
  },

  methods: {
    ...mapMutations('base/crud', ['setIsVisible', 'setCriteria', 'setItem', 'setIndex']),
    ...mapActions('tax/taxWarrants', TAX_WARRANTS_ACTIONS),

    formatWarrantNumber,

    searchByCriteria() {
      this.$emit('loadCRUDList');
    },

    dataFromCommonDropDowns(val, whichField) {
      this.criteriaState[whichField] = val;
      const returnCriteria = makeCriteriaStateNullIfEmptyString(this.criteriaState);
      this.criteriaState = { ...returnCriteria };
      this.searchByCriteria();
    },
  },
};
